import { useMediaQuery } from "@/hooks/use-media-query";
import * as React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerFooter,
  DrawerClose,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "./ui/drawer";
import { CaretDownIcon, CaretUpIcon } from "@radix-ui/react-icons";
import XIcon from "@mui/icons-material/X";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";
import InstagramIcon from "@mui/icons-material/Instagram";
import { Button, buttonVariants } from "./ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

const components: { title: string; href: string; description: string }[] = [
  {
    title: "Trace-BIM",
    href: "/projects/trace-bim",
    description: "Raster plan to validated, Revit-aware geometry.",
  },
  {
    title: "Architecture",
    href: "/projects/arch",
    description: "Real world architectural projects that I have worked on.",
  },
  {
    title: "Virtual Architecture",
    href: "/projects/virtualArch",
    description:
      "Virtual architectural projects with OVA.DESIGN which I co-founded.",
  },
  {
    title: "Product Design",
    href: "/projects/productDesign",
    description: "Projects that I do for fun. Mostly printable 3D models.",
  },
  {
    title: "Web Development",
    href: "/about",
    description: "My web design and development projects.",
  },
];
const socials: {
  key: number;
  icon: React.ReactNode;
  href: string;
  description: string;
}[] = [
  {
    key: 1,
    icon: <LinkedInIcon className="m-2" />,
    href: "https://linkedin.com/in/parker-cai-463216a2",
    description: "LinkedIn",
  },
  {
    key: 2,
    icon: <InstagramIcon className="m-2" />,
    href: "https://instagram.com/parkercai",
    description: "Instagram",
  },
  {
    key: 3,
    icon: <XIcon className="m-2" />,
    href: "https://twitter.com/parkercai",
    description: "Twitter",
  },
  {
    key: 4,
    icon: <GitHubIcon className="m-2" />,
    href: "https://github.com/parkercai",
    description: "Github",
  },
];

export function NavigationMenuHeader() {
  // useMediaQuery Hook to remove drawer component from the DOM
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Array containing navigation items
  const navItems = [
    { id: 1, text: "Home", url: "/" },
    { id: 2, text: "About", url: "/about" },
    { id: 3, text: "Links", url: "/links" },
    { id: 4, text: "Resume", url: "/resume" },
    { id: 5, text: "Projects", url: "/projects" },
    { id: 6, text: "Contact", url: "/contact" },
  ];

  return isDesktop ? (
    <NavigationMenu className="fixed inset-x-0 top-0 z-[2147483647] m-3 w-auto">
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger>Parker Cai</NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
              <li className="row-span-4">
                <NavigationMenuLink asChild className="relative">
                  <Link
                    className="flex h-full w-full select-none flex-col justify-end rounded-control p-6 no-underline outline-none focus:shadow-md"
                    to="/"
                  >
                    <img
                      src="/images/about/ParkerAvatarPhoto.webp"
                      alt="profile picture"
                      className="absolute inset-0 h-full w-full rounded-control object-cover"
                    />
                    <p className="z-10 text-sm leading-tight text-muted-foreground text-center whitespace-nowrap">
                      I build things
                    </p>
                  </Link>
                </NavigationMenuLink>
              </li>

              <ListItem href="/" title="Home">
                Welcome to my 3D playground.
              </ListItem>

              <ListItem href="/about" title="About">
                My background. Who I am, what I do and my projects. (Tech
                Resume)
              </ListItem>

              <ListItem href="/resume" title="Resume">
                A simple version of my resume. Downloadable for your reference.
              </ListItem>

              <li className="flex justify-evenly">
                {socials.map((social) => (
                  <HoverCard key={social.description}>
                    <HoverCardTrigger asChild>
                      <ListItem
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        external
                      >
                        {social.icon}
                      </ListItem>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-auto p-2 text-center text-sm text-muted-foreground">
                      {social.description}
                    </HoverCardContent>
                  </HoverCard>
                ))}
              </li>
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <NavigationMenuTrigger>Projects</NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] ">
              {components.map((component) => (
                <ListItem
                  key={component.title}
                  title={component.title}
                  href={component.href}
                >
                  {component.description}
                </ListItem>
              ))}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <NavigationMenuLink
            asChild
            className={navigationMenuTriggerStyle()}
          >
            <Link to="/contact">Contact</Link>
          </NavigationMenuLink>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  ) : (
    // Mobile Navigation Drawer Menu
    <Drawer direction="bottom">
      <DrawerTrigger
        className={`${buttonVariants({
          variant: "secondary",
        })} fixed bottom-6 left-1/2 z-10 float-none -translate-x-1/2 overflow-hidden`}
      >
        <CaretUpIcon className="h-8 w-8" />
        <DrawerContent className="mx-auto w-auto items-center bg-transparent backdrop-blur">
          <DrawerHeader>
            <Link to="/">
              <DrawerTitle>Parker Cai</DrawerTitle>
            </Link>
            <DrawerDescription>Portfolio Site</DrawerDescription>
          </DrawerHeader>
          <div className="py-18">
            <ul>
              {navItems.map((item) => (
                <li key={item.id} className="py-2 text-center">
                  <Button size={"lg"} className="w-60 text-2xl" asChild>
                    <Link to={item.url}>{item.text}</Link>
                  </Button>
                </li>
              ))}
            </ul>
          </div>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="secondary">
                <CaretDownIcon className="h-8 w-8" />
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </DrawerTrigger>
    </Drawer>
  );
}

interface ListItemProps extends React.ComponentPropsWithoutRef<"a"> {
  title?: string;
  external?: boolean;
}

const ListItem = React.forwardRef<React.ElementRef<"a">, ListItemProps>(
  ({ className, title, children, href, external, ...props }, ref) => {
    const content = (
      <>
        {title && (
          <div className="text-sm font-medium leading-none">{title}</div>
        )}
        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
          {children}
        </p>
      </>
    );

    return (
      <li>
        <NavigationMenuLink asChild>
          {external ? (
            <a
              ref={ref}
              href={href}
              className={cn(
                "block select-none space-y-1 rounded-control p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                className,
              )}
              {...props}
            >
              {content}
            </a>
          ) : (
            <Link
              to={href || "/"}
              className={cn(
                "block select-none space-y-1 rounded-control p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                className,
              )}
            >
              {content}
            </Link>
          )}
        </NavigationMenuLink>
      </li>
    );
  },
);
ListItem.displayName = "ListItem";
